import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import { FOCUS_RING, FOCUS_RING_OFFSET } from '../../focusRing';

/**
 * The one look every segmented switch shares — SegmentedNav between pages, SegmentedControl between choices on a
 * page: a grey track of equal segments, and a Primary highlight that slides to the chosen one.
 *
 * Equal segments keep the slide a pure CSS transform, with nothing to measure.
 */

/** Minimum segment height for a fine pointer. A coarse pointer gets the theme's medium control height. */
export const SEGMENT_HEIGHT = 28;

/** Fewer than this and there is nothing to switch between. */
export const MIN_SEGMENTS = 2;

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const FORCED_COLORS = '@media (forced-colors: active)';

const slide = (theme: Theme, property: string): string =>
  theme.transitions.create(property, { duration: theme.transitions.duration.standard, easing: theme.transitions.easing.easeInOut });

/** The track: equal columns, so the highlight can move a whole segment at a time. */
export const segmentTrackSx: SystemStyleObject<Theme> = {
  position: 'relative',
  display: 'grid',
  gridAutoFlow: 'column',
  gridAutoColumns: 'minmax(0, 1fr)',
  listStyle: 'none',
  m: 0,
  p: 1,
  borderRadius: 5,
  bgcolor: 'Neutrals.50',
};

/** The Primary highlight under segment `index` of `count`. Decorative: the segment itself says it is chosen. */
export const segmentHighlightSx =
  (count: number, index: number) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    position: 'absolute',
    insetBlock: theme.spacing(1),
    insetInlineStart: theme.spacing(1),
    width: `calc((100% - ${theme.spacing(2)}) / ${count})`,
    transform: `translateX(${index * 100}%)`,
    borderRadius: 4,
    bgcolor: 'primary.main',
    boxShadow: theme.customShadows.cardResting,
    transition: slide(theme, 'transform'),
    [REDUCED_MOTION]: { transition: 'none' },
  });

/** One segment — a link or a button. `touchHeight` defaults to the theme's medium control height: a thumb-sized target. */
export const segmentSx =
  (chosen: boolean, height: number, touchHeight?: number) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    position: 'relative',
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: height,
    px: 3,
    borderRadius: 4,
    color: chosen ? 'primary.contrastText' : 'Neutrals.700',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.label.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center',
    textDecoration: 'none',
    overflowWrap: 'normal',
    textWrap: 'balance',
    transition: slide(theme, 'color'),
    '&:hover': { textDecoration: 'none', color: chosen ? 'primary.contrastText' : 'Neutrals.900' },
    '&:focus-visible': { outline: FOCUS_RING, outlineOffset: FOCUS_RING_OFFSET },
    '&.Mui-disabled': chosen ? {} : { color: 'text.disabled' },
    '@media (pointer: coarse)': { minHeight: touchHeight ?? theme.controlHeight.medium, minWidth: theme.controlHeight.medium },
    [REDUCED_MOTION]: { transition: 'none' },
  });

const EDGE_COVER_SPACING = 8;

const EDGE_SHADE_SPACING = 3;

const EDGE_SHADE_OPACITY = 0.16;

const EDGE_COVER_SOLID = '30%';

export const segmentScrollTrackSx = (theme: Theme): SystemStyleObject<Theme> => {
  const surface = theme.palette.Neutrals[50];
  const clear = alpha(surface, 0);
  const shade = alpha(theme.palette.Neutrals[900], EDGE_SHADE_OPACITY);
  const cover = theme.spacing(EDGE_COVER_SPACING);
  const edge = theme.spacing(EDGE_SHADE_SPACING);
  return {
    width: 'fit-content',
    maxWidth: '100%',
    gridAutoColumns: 'max-content',
    overflowX: 'auto',
    overscrollBehaviorX: 'contain',
    scrollSnapType: 'x proximity',
    scrollPaddingInline: theme.spacing(1),
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    backgroundImage: [
      `linear-gradient(to right, ${surface} ${EDGE_COVER_SOLID}, ${clear})`,
      `linear-gradient(to left, ${surface} ${EDGE_COVER_SOLID}, ${clear})`,
      `linear-gradient(to right, ${shade}, ${clear})`,
      `linear-gradient(to left, ${shade}, ${clear})`,
    ].join(', '),
    backgroundPosition: 'left center, right center, left center, right center',
    backgroundSize: `${cover} 100%, ${cover} 100%, ${edge} 100%, ${edge} 100%`,
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'local, local, scroll, scroll',
  };
};

export const segmentContentSx =
  (chosen: boolean) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    flex: 'none',
    whiteSpace: 'nowrap',
    scrollSnapAlign: 'start',
    bgcolor: chosen ? 'primary.main' : 'transparent',
    boxShadow: chosen ? theme.customShadows.cardResting : 'none',
    transition: theme.transitions.create(['color', 'background-color'], {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }),
    [REDUCED_MOTION]: { transition: 'none' },
    [FORCED_COLORS]: chosen ? { outline: '1px solid Highlight' } : {},
  });

export const segmentCountSx =
  (chosen: boolean) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    ml: 2,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: theme.typography.fontWeightRegular,
    color: chosen ? 'primary.contrastText' : 'Neutrals.600',
  });
